import {useEffect} from 'react';

import {ModalityCodeEnum} from 'domain/usecases';
import {QueryParamsEnum, QueryParamsForTaggingEnum} from 'presentation/constants';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {useRefundRequest} from 'presentation/pages/private/refund/hooks';
import {PersonTypeEnum} from '../../components/ReceiptRegistration/components/ProviderData/constants/types';
import {useListRequestFormsStore} from '../../store/useListRequestFormsStore';
import {useRequestDataStore} from '../../store/useRequestDataStore';
import {useSelectedRequestIndexStore} from '../../store/useSelectedRequestIndexStore';
import {ModalityQueryParamsEnum, TypeProviderQueryEnum} from './constants';
import {UseQueryParamsSyncProps} from './types';
import {useTherapies} from '../../components/Therapies/hooks/useTherapies';

const getServiceTypeLabel = (modalityCode: ModalityCodeEnum): string => {
	const serviceTypeMap = {
		[ModalityCodeEnum.MEDICAL_APPOINTMENT]: ModalityQueryParamsEnum.MEDICAL_APPOINTMENT,
		[ModalityCodeEnum.SPECIAL_EXAMS]: ModalityQueryParamsEnum.SPECIAL_EXAMS,
		[ModalityCodeEnum.SIMPLE_EXAMS]: ModalityQueryParamsEnum.SIMPLE_EXAMS,
		[ModalityCodeEnum.HOSPITALIZATIONS]: ModalityQueryParamsEnum.HOSPITALIZATIONS,
		[ModalityCodeEnum.EMERGENCY]: ModalityQueryParamsEnum.EMERGENCY,
		[ModalityCodeEnum.THERAPIES]: ModalityQueryParamsEnum.THERAPIES,
	};

	return serviceTypeMap[modalityCode] || modalityCode;
};

const getProviderTypeLabel = (professionalType: PersonTypeEnum): string => {
	const providerTypeMap = {
		[PersonTypeEnum.PHYSICAL_PERSON]: TypeProviderQueryEnum.PHYSICAL_PERSON,
		[PersonTypeEnum.LEGAL_PERSON]: TypeProviderQueryEnum.LEGAL_PERSON,
		[PersonTypeEnum.F]: TypeProviderQueryEnum.PHYSICAL_PERSON,
		[PersonTypeEnum.J]: TypeProviderQueryEnum.LEGAL_PERSON,
	};

	return providerTypeMap[professionalType] || professionalType;
};

export function useRefundQueryParamsSync({step, firstVisit}: UseQueryParamsSyncProps) {
	const {setSearchParams} = useQueryParams();
	const {atoms, isTherapiesFlowEnabled} = useRefundRequest();
	const {isEmptyTherapies, isEditMode} = useTherapies();
	const requestData = useRequestDataStore(state => state.data);
	const requestForms = useListRequestFormsStore(state => state.list);
	const selectedRequestIndex = useSelectedRequestIndexStore(state => state.index);

	const currentForm = requestForms[selectedRequestIndex] || requestForms[0];
	const professionalType = currentForm?.provider?.professionalType;
	const specialty = currentForm?.patient?.specialty;
	const state = currentForm?.fiscal?.state;
	const city = currentForm?.fiscal?.city;
	const canEmitFiscalDocument = currentForm?.fiscal?.canEmitFiscalDocument;

	useEffect(() => {
		setSearchParams(
			prev => {
				prev.set(QueryParamsForTaggingEnum.REQUEST_REFUND_STEP, (step + 1).toString());
				prev.set(QueryParamsForTaggingEnum.FIRST_VISIT, firstVisit ? 'true' : 'false');

				if (step >= 2 && atoms.treatmentData?.modalityCode) {
					prev.set(
						QueryParamsForTaggingEnum.REQUEST_REFUND_TYPE_SERVICE,
						getServiceTypeLabel(atoms.treatmentData.modalityCode),
					);
				} else {
					prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_TYPE_SERVICE);
				}

				if (step >= 2 && professionalType) {
					prev.set(
						QueryParamsForTaggingEnum.REQUEST_REFUND_TYPE_PROVIDER,
						getProviderTypeLabel(professionalType),
					);
				} else {
					prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_TYPE_PROVIDER);
				}

				if (specialty?.description) {
					prev.set(
						QueryParamsForTaggingEnum.REQUEST_REFUND_SPECIALITY,
						specialty.description,
					);
				} else {
					prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_SPECIALITY);
				}

				if (canEmitFiscalDocument === 'yes' && state?.state) {
					prev.set(QueryParamsForTaggingEnum.REQUEST_REFUND_UF, state.state);
				} else {
					prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_UF);
				}

				if (canEmitFiscalDocument === 'yes' && city?.city) {
					prev.set(QueryParamsForTaggingEnum.REQUEST_REFUND_CITY, city.city);
				} else {
					prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_CITY);
				}

				if (requestData?.protocol) {
					prev.set(
						QueryParamsForTaggingEnum.REQUEST_REFUND_PROTOCOL_NUMBER,
						requestData.protocol,
					);
				} else {
					prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_PROTOCOL_NUMBER);
				}

				if (step === 3 && isTherapiesFlowEnabled && isEmptyTherapies) {
					prev.set(QueryParamsEnum.REFUND_THERAPY_MODE, isEditMode ? 'edit' : 'add');
				}

				return prev;
			},
			{
				replace: true,
			},
		);
		return () => {
			setSearchParams(prev => {
				prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_STEP);
				prev.delete(QueryParamsForTaggingEnum.FIRST_VISIT);
				prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_TYPE_SERVICE);
				prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_TYPE_PROVIDER);
				prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_SPECIALITY);
				prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_UF);
				prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_CITY);
				prev.delete(QueryParamsForTaggingEnum.REQUEST_REFUND_PROTOCOL_NUMBER);
				prev.delete(QueryParamsEnum.REFUND_THERAPY_MODE);
				return prev;
			});
		};
	}, [
		step,
		atoms.treatmentData?.modalityCode,
		professionalType,
		specialty?.description,
		state?.state,
		city?.city,
		canEmitFiscalDocument,
		requestData?.protocol,
		setSearchParams,
		firstVisit,
	]);

	return {
		step,
		setSearchParams,
	};
}
